import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable, Observer, throwError, Subject, of} from 'rxjs';
import {catchError, map, takeUntil} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import { RestUrlsConfig, GenericConfig } from '@signature-it/ngx-generic/config';
import { LanguageService } from '@signature-it/ngx-generic/services';
import { EntityTreeService } from '@signature-it/ngx-entity/services';
import { SolrOptionsEntityDTO } from '@signature-it/ngx-entity/models';
import * as EntityActions from '@signature-it/ngx-entity/store/actions';
import { EntitySelectors } from '@signature-it/ngx-entity/store/selectors';
import { SolrEntityService } from "@signature-it/ngx-entity/services";
import {ProductFieldDTO, Group, GroupUserLinksFields, CatalogueBlockDTO} from '@signature-it/ngx-catalogue/models';
import {CatalogueActions} from '@signature-it/ngx-catalogue/store/actions';
import {CatalogueSelectors} from '@signature-it/ngx-catalogue/store/selectors';
import {UtilityService} from './utility.service';
import { asapScheduler } from 'rxjs';

declare let _: any;

@Injectable({
    providedIn: 'root'
})
export class GroupApiService {

    groupUserLinksFields$: Observable<GroupUserLinksFields[]>;
    loadingUserLinks$: Observable<boolean>;
    currentGroup$: Observable<Group>;
    getGroupsTree$: Observable<Group>;
    loadingGroupsTree$: Observable<boolean>;
    groupsTree: Group;
    groupsTags: Object;

    constructor (
        protected http: HttpClient,
        protected restUrlsConfig: RestUrlsConfig,
        protected store$: Store<any>,
        protected utilitySvc: UtilityService,
        protected langSvc: LanguageService,
        protected entityTreeService: EntityTreeService,
        protected solrEntityService: SolrEntityService) {
        this.groupUserLinksFields$ = this.store$.select(CatalogueSelectors.getGroupUserLinksFields);
        this.loadingUserLinks$ = this.store$.select(CatalogueSelectors.getLoadingUserLinks);
        this.currentGroup$ = this.store$.select(CatalogueSelectors.getCurrentGroup);
        this.getGroupsTree$ = this.store$.select(CatalogueSelectors.getGroupsTree);
        this.loadingGroupsTree$ = this.store$.select(CatalogueSelectors.loadingGroupsTree);
    }

    initGroupsTree(taxonomy?) {
        const action = CatalogueActions.getGroupsTree(taxonomy);
        this.store$.dispatch(action);
    }

    initManualGroupsTree(group) {
        const action = CatalogueActions.gotGroupsTreeSuccess(group);
        this.store$.dispatch(action);
    }

    getUserLinks(group:number) {
        return this.http.get(`${this.restUrlsConfig.appURL}/public/group/${group}/userLinks.json?global=true`)
        // .pipe(map((res) => res));
    }

    getContext(group:number,contextId:number): Observable<any> {
        const currLang = this.langSvc.getCurrentLanguage();
        return this.http.get(`${this.restUrlsConfig.appURL}/public/group/${group.toString()}/fieldLinks.json?lang=1&context_id=${contextId}&lang=${currLang.code}`)
    }

    /**
     * @param groupId
     * @returns Observable<ProductFieldDTO[]>
     */
    getUserLinksFields({groupId, taxonomy, entityVar}):Observable<GroupUserLinksFields>  {
        const currLang = this.langSvc.getCurrentLanguage();
        let url = `${this.restUrlsConfig.appURL}/public/group/${groupId}/userLinks.json?global=true&lang=${currLang.code}`;
        if (taxonomy) {
            url = `${url}&taxonomy=${taxonomy}&entityVar=${entityVar}`;
        }
        return this.http.get(url)
            .pipe(map((res: any) => new GroupUserLinksFields({id: groupId, fields: res['data'], taxonomy, entityVar})), catchError(error => throwError(error)));
    }


    getUserLinksByFields(groupId, fields):Observable<ProductFieldDTO[]> {
        const currLang = this.langSvc.getCurrentLanguage();
        return this.http.get(`${this.restUrlsConfig.appURL}/public/group/${groupId}/userLinks.json?fields=${fields}&lang=${currLang.code}`)
            .pipe(map((res: any) => new GroupUserLinksFields({id: groupId, fields: res['data']}).fields), catchError(error => throwError(error)));
    }

    getGroupByAttr(attr, decodeSeo = false):Group {
        return this.utilitySvc.findDeepByAttr(this.groupsTree, attr, decodeSeo);
    }

    getUserLinksByGroup(groupId:number, taxonomy?, entityVar?) {
        taxonomy = taxonomy || '';
        entityVar = entityVar || '';
        const unSubscriber = new Subject();
        this.loadingUserLinks$.pipe(takeUntil(unSubscriber)).subscribe((loadingUserLinks) => {
            if (!loadingUserLinks) {
                this._getUserLinksAction(groupId, taxonomy, entityVar);
                unSubscriber.next(true);
                unSubscriber.complete();
            }
        });

        return new Observable((observer: Observer<any>) => {
            let sub;
            sub = this.groupUserLinksFields$.subscribe((groupUserLinksFields: GroupUserLinksFields[]) => {
                if (groupUserLinksFields && groupUserLinksFields.length && _.filter(groupUserLinksFields, {id: groupId, taxonomy, entityVar})[0]) {
                    if(sub) {
                        sub.unsubscribe();
                    }
                    observer.next(_.filter(groupUserLinksFields, {id: groupId, taxonomy, entityVar})[0].fields);
                }
            });
        });
    }

    getParentGroupIds(group: Group) {
        const ids = [group.id, group.parent];
        const parentGroup: Group = this.getGroupByAttr({id: group.parent});
        if(parentGroup){
            ids.push(parentGroup.parent);
            if(parentGroup.parent !== 1) {
                ids.push(1);
            }
        }
        return ids;
    }


    getCurrentGroup(groupId, getAdditionalData = false): Observable<Group> {
        groupId = parseInt(groupId);
        this._getCurrentGroupAction(groupId, getAdditionalData);
        let unsubscriber = new Subject();
        return new Observable((observer: Observer<any>) => {
            this.currentGroup$.pipe(takeUntil(unsubscriber)).subscribe((group: Group) => {
                if(group && groupId == group.id) {
                    unsubscriber.next(true);
                    unsubscriber.complete();
                    observer.next(group);
                }
            });
        });
    }

    getGroupsTree(taxonomyUri): Observable<any> {
        const currLang = this.langSvc.getCurrentLanguage();
        const seoParam = GenericConfig.disableSeo ? '' : '&seo';
        if (!taxonomyUri) {
            return this.http.get<any[]>(`${this.restUrlsConfig.appURL}/public/group/1/groupTree.json?depth=5${seoParam}&lang=${currLang.code}`)
                .pipe(map((res) => {
                    const groupsTree = new Group(res['data'], true);
                    this.groupsTree = groupsTree;
                    return groupsTree;
                }), catchError((error: any) => throwError(error)));
        }

        return this.entityTreeService.getTreeByTaxUri(GenericConfig.maximumTaxonomyLevelsInCatalog ?? 5, taxonomyUri).pipe(map((res) => {
            if(res[0]) {
                res[0]['taxonomyUri'] = taxonomyUri;
            }
            const groupsTree = new Group(res[0], true);
            this.groupsTree = groupsTree;
            return groupsTree;
        }), catchError((error: any) => throwError(error)));
    }

    getGroupsTags() {
        return this.http.get<any[]>(`${this.restUrlsConfig.appURL}/public/group/tags.json`)
            .pipe(map((res) => {

                this.groupsTags = res;
                return res;
            }), catchError((error: any) => throwError(error)));
    }

    getAdditionalData(group) {
        const childrenIds = !group.isleaf ? group.children.filter(c => !c.doc).map((c) => c.id) : [];
        const entitiesIds = !group.doc ? [group.id].concat(childrenIds) : childrenIds;
        const entityId = entitiesIds.length == 1 ? entitiesIds[0] : `(${entitiesIds.join(' OR ')})`;
        const options = new SolrOptionsEntityDTO({
            additionalFQ: [`entity_id_i: ${entityId}`]
        })
        return this.solrEntityService.getSolr(options);
    }

    getGroupRootId() {
        return new Observable(observer => {
            observer.next(1);
        });
    }

    getGroupTreeOnStandAloneMode() {
        return true;
    }

    getCatalogueBlocks(groupId) {
        return this.http.get<any[]>(`${this.restUrlsConfig.appURL}/public/group/catalogue_blocks/${groupId}.json`)
            .pipe(map((res) => {
                let catalogueBlocks = [];
                res.forEach(item => {
                    catalogueBlocks.push(new CatalogueBlockDTO(item))
                });
                return catalogueBlocks;
            }), catchError((error: any) => throwError(error)));
    }

    /** Actions */
    protected _getUserLinksAction(groupId: number, taxonomy?, entityVar?) {
        const action = CatalogueActions.getGroupUserLinks(groupId, taxonomy, entityVar);
        asapScheduler.schedule(() => this.store$.dispatch(action));
    }

    protected _getCurrentGroupAction(groupId: number, getAdditionalData: boolean) {
        const action = CatalogueActions.getCurrentGroup(groupId, getAdditionalData);
        this.store$.dispatch(action);
    }

}
